/* eslint-disable complexity, max-statements */
import { useMemo } from 'react';

import Label from '@mindtickle/label';

import { InlineSkeleton } from '@mindtickle/skeleton';
import { TEXT_COLORS, TEXT_TYPES } from '@mindtickle/styles/lib/classNames';

import { useNavigationConfig } from '../../hooks/useNavigationConfig';
import { MenuContentPopoverProps, NavRouteType } from '../../types';
import toHyphenatedString from '../../utils/toHyphenatedString';
import { getFormattedName } from '../../utils/getFormattedName';
import NavMenuItem from '../NavMenuItem';
import { StyledMenuContent, StyledNavLoaderItem, loaderStyle } from './styles';

type RenderPopoverItemArgs = {
  item: NavRouteType;
  intl: any;
  menuId: string;
  activeRouteId: string;
  isLoading: boolean;
  isSubMenuVisible: Record<string, boolean>;
  subMenuMessages: Record<string, any>;
};

const renderPopoverItem = ({
  item,
  intl,
  menuId,
  activeRouteId,
  isLoading,
  isSubMenuVisible,
  subMenuMessages,
}: RenderPopoverItemArgs) => {
  const { link, id, showAfterPermissionsCheck } = item;
  if (!id) return null;

  const isGated = Boolean(showAfterPermissionsCheck);
  const isAllowed = isSubMenuVisible[id] ?? !isGated;

  if (isGated && isLoading) {
    return (
      <StyledNavLoaderItem
        key={id}
        className="nav-submenu-loader"
        data-testid={`submenu-loader-${toHyphenatedString({
          str: id,
        })}`}
      >
        <InlineSkeleton style={loaderStyle} />
      </StyledNavLoaderItem>
    );
  }

  if (isGated && !isAllowed) {
    return null;
  }

  const messageConfig = subMenuMessages[id];

  return (
    <NavMenuItem
      dataTestId={`submenu-${toHyphenatedString({
        str: id,
      })}`}
      key={id}
      link={link}
      isActive={activeRouteId === id}
      isPopoverContentLink
      name={getFormattedName({
        intl,
        menuId: id ?? menuId,
        messageConfig,
      })}
    />
  );
};

type BuildPopoverItemsArgs = {
  menuId: string;
  subMenu: NavRouteType[];
  intl: any;
  activeRouteId: string;
  isLoading: boolean;
  isSubMenuVisible: Record<string, boolean>;
  menuConfig: any;
};

const buildPopoverItems = ({
  menuId,
  subMenu,
  intl,
  activeRouteId,
  isLoading,
  isSubMenuVisible,
  menuConfig,
}: BuildPopoverItemsArgs) => {
  if (!menuConfig) return null;

  const subMenuMessages = menuConfig.subMenuName;

  return subMenu
    .filter(({ id }) => id && subMenuMessages[id])
    .map(item =>
      renderPopoverItem({
        item,
        intl,
        menuId,
        activeRouteId,
        isLoading,
        isSubMenuVisible,
        subMenuMessages,
      })
    );
};

const MenuContentPopover = ({
  subMenu,
  menuName,
  menuId,
  intl,
  activeRouteId,
  isLoading,
  isSubMenuVisible,
}: MenuContentPopoverProps) => {
  const { navigationConfig } = useNavigationConfig();
  const menuConfig = navigationConfig[menuId];

  const items = useMemo(
    () =>
      buildPopoverItems({
        menuId,
        subMenu,
        intl,
        activeRouteId,
        isLoading,
        isSubMenuVisible,
        menuConfig,
      }),
    [activeRouteId, intl, isLoading, isSubMenuVisible, menuConfig, menuId, subMenu]
  );

  return (
    <StyledMenuContent>
      <Label
        color={TEXT_COLORS.DEFAULT}
        type={TEXT_TYPES.HEADING_SMALL}
        className="menu-content-title"
      >
        {menuName}
      </Label>
      <div data-testid="navigation-popover-menu-item-wrapper" className="menu-content-wrapper">
        {items}
      </div>
    </StyledMenuContent>
  );
};

export default MenuContentPopover;
